import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Space, Typography, Row, Col, Statistic, Progress, Button, Spin, Alert } from 'antd';
import { ArrowUpOutlined } from '@ant-design/icons';
import TierBadge from './TierBadge';
import EnvironmentBadge from './EnvironmentBadge';
import ConsoleLink from './ConsoleLink';
import ResourceCard from './ResourceCard';
import TenantComputeSettings from './TenantComputeSettings';
import logger from '../../utils/logger';

const { Title, Text, Paragraph } = Typography;

const TenantDetailsPage = () => {
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tenantData, setTenantData] = useState(null);

  // Mock data - will be replaced with actual API call (memoized to prevent unnecessary re-renders)
  const mockTenantData = useMemo(() => ({
    tenant: {
      tenant_id: tenantId || "acme_corp",
      name: "Acme Corporation",
      tier: "business",
      status: "READY",
      created_at: "2025-01-15T10:00:00Z"
    },
    resources: {
      environment: "local",
      is_local: true,
      kms_keys: [
        {
          key_id: "local://./local_keys/acme_corp_s3.key",
          display_id: "Local Key (Dev Mode)",
          key_type: "s3",
          enabled: true,
          is_local: true,
          metadata: {
            alias: "rlc-acme-s3",
            algorithm: "Fernet (AES-128-CBC)",
            rotation_enabled: false
          }
        },
        {
          key_id: "local://./local_keys/acme_corp_rds.key",
          display_id: "Local Key (Dev Mode)",
          key_type: "rds",
          enabled: true,
          is_local: true,
          metadata: {
            alias: "rlc-acme-rds",
            algorithm: "Fernet (AES-128-CBC)",
            rotation_enabled: false
          }
        }
      ],
      s3_bucket: {
        bucket_name: "local://./local_storage/shared",
        display_name: "Local Storage (Dev Mode)",
        encryption_type: "kms",
        size_gb: 0.5,
        object_count: 12,
        is_local: true,
        metadata: {
          key_prefix: `tenants/${tenantId || "acme_corp"}/`,
          versioning: "Disabled",
          region: "local"
        }
      },
      database: {
        instance_id: "local://shared",
        display_id: "Local Database (Dev Mode)",
        instance_type: "shared",
        endpoint: "localhost:5432/rlc_spark",
        port: 5432,
        multi_az: false,
        encryption_enabled: true,
        backup_retention_days: 7,
        storage_gb: 100,
        is_local: true,
        metadata: {}
      },
      vpc: null
    },
    cost: null,
    metrics: {
      storage: {
        size_gb: 0.5,
        object_count: 12
      },
      database: {
        connections: null,
        cpu_percent: null
      }
    }
  }), [tenantId]);

  const fetchTenantDetails = async () => {
    try {
      // TODO: Replace with actual API call when backend is ready
      // const response = await tenantService.getTenantDetails(tenantId);
      // setTenantData(response.data);

      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setTenantData(mockTenantData);
    } catch (error) {
      logger.error('Error fetching tenant details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenantDetails();
  }, [tenantId, mockTenantData]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tenantData) {
    return <Alert message="Tenant not found" type="error" />;
  }

  const { tenant, resources, cost, metrics } = tenantData;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={2} style={{ margin: 0 }}>
                Tenant Details: {tenant.name}
              </Title>
              <Space>
                <EnvironmentBadge isLocal={resources.is_local} />
                <Button type="primary" icon={<ArrowUpOutlined />}>
                  Upgrade Tier
                </Button>
              </Space>
            </div>

            <Descriptions column={4}>
              <Descriptions.Item label="Tenant ID">{tenant.tenant_id}</Descriptions.Item>
              <Descriptions.Item label="Tier">
                <TierBadge tier={tenant.tier} />
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Text type="success">✅ {tenant.status}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {new Date(tenant.created_at).toLocaleDateString()}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        <Alert
          message="Using Mock Data"
          description="Backend APIs are pending. This page displays sample data for development."
          type="info"
          showIcon
          closable
        />

        {/* Compute Configuration */}
        <TenantComputeSettings
          tenant={tenant}
          onRefresh={fetchTenantDetails}
        />

        {/* KMS Keys */}
        <ResourceCard title="Encryption Keys" icon="🔑">
          {resources.kms_keys.map((key, idx) => (
            <Card
              key={idx}
              type="inner"
              title={`${key.key_type.toUpperCase()} Key`}
              extra={
                <Space>
                  <Text type="success">✅ Enabled</Text>
                  <ConsoleLink url={key.metadata.console_url} isLocal={key.is_local} />
                </Space>
              }
              style={{ marginBottom: 8 }}
            >
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Key ID">
                  <code>{key.display_id}</code>
                </Descriptions.Item>
                <Descriptions.Item label="Alias">{key.metadata.alias}</Descriptions.Item>
                <Descriptions.Item label="Algorithm">
                  {key.metadata.algorithm || 'AES-256'}
                </Descriptions.Item>
                <Descriptions.Item label="Rotation">
                  {key.metadata.rotation_enabled ? '✅ Enabled' : '❌ Disabled'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ))}
        </ResourceCard>

        {/* S3 Storage */}
        <ResourceCard
          title="S3 Storage"
          icon="🪣"
          extra={<ConsoleLink url={resources.s3_bucket.metadata.console_url} isLocal={resources.is_local} />}
        >
          <Descriptions column={2}>
            <Descriptions.Item label="Bucket">{resources.s3_bucket.display_name}</Descriptions.Item>
            <Descriptions.Item label="Encryption">
              {resources.s3_bucket.encryption_type.toUpperCase()}
              {resources.s3_bucket.encryption_type === 'kms' && ' (Customer-managed)'}
            </Descriptions.Item>
            <Descriptions.Item label="Key Prefix">
              <code>{resources.s3_bucket.metadata.key_prefix}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Versioning">
              {resources.s3_bucket.metadata.versioning}
            </Descriptions.Item>
            <Descriptions.Item label="Size">
              {resources.s3_bucket.size_gb} GB
            </Descriptions.Item>
            <Descriptions.Item label="Objects">
              {resources.s3_bucket.object_count}
            </Descriptions.Item>
            <Descriptions.Item label="Region">
              {resources.s3_bucket.metadata.region}
            </Descriptions.Item>
          </Descriptions>
        </ResourceCard>

        {/* Database */}
        <ResourceCard
          title="Database"
          icon="🗄️"
          extra={<ConsoleLink url={resources.database.metadata.console_url} isLocal={resources.is_local} />}
        >
          <Descriptions column={2}>
            <Descriptions.Item label="Instance ID">{resources.database.display_id}</Descriptions.Item>
            <Descriptions.Item label="Type">
              {resources.database.instance_type} {resources.database.instance_type !== 'shared' && '(Dedicated)'}
            </Descriptions.Item>
            <Descriptions.Item label="Endpoint">
              <code>{resources.database.endpoint}</code>
            </Descriptions.Item>
            <Descriptions.Item label="Port">{resources.database.port}</Descriptions.Item>
            <Descriptions.Item label="Multi-AZ">
              {resources.database.multi_az ? '✅ Yes' : '❌ No'}
            </Descriptions.Item>
            <Descriptions.Item label="Encryption">
              {resources.database.encryption_enabled ? '✅ Enabled (KMS)' : '❌ Disabled'}
            </Descriptions.Item>
            <Descriptions.Item label="Backups">
              {resources.database.backup_retention_days} days retention
            </Descriptions.Item>
            <Descriptions.Item label="Storage">
              {resources.database.storage_gb} GB
            </Descriptions.Item>
          </Descriptions>
        </ResourceCard>

        {/* Cost & Metrics */}
        <Row gutter={16}>
          {!resources.is_local && cost && (
            <Col span={12}>
              <ResourceCard title="Estimated Monthly Cost" icon="💰">
                <Statistic
                  value={cost.estimated_monthly_usd}
                  precision={2}
                  prefix="$"
                  suffix="USD"
                />
                <Paragraph type="secondary" style={{ marginTop: 16 }}>
                  {cost.note}
                </Paragraph>
              </ResourceCard>
            </Col>
          )}

          <Col span={resources.is_local ? 24 : 12}>
            <ResourceCard title="Resource Metrics (Last 24h)" icon="📊">
              <Space direction="vertical" style={{ width: '100%' }}>
                {metrics.storage && (
                  <div>
                    <Text>Storage Used: {metrics.storage.size_gb} GB</Text>
                    {tenant.tier === 'business' && (
                      <Progress percent={(metrics.storage.size_gb / 500) * 100} status="normal" />
                    )}
                  </div>
                )}
                {metrics.database.connections !== null && (
                  <div>
                    <Text>DB Connections: {metrics.database.connections} / 100</Text>
                    <Progress percent={(metrics.database.connections / 100) * 100} status="normal" />
                  </div>
                )}
                {metrics.database.cpu_percent !== null && (
                  <div>
                    <Text>DB CPU: {metrics.database.cpu_percent}%</Text>
                    <Progress percent={metrics.database.cpu_percent} status="normal" />
                  </div>
                )}
                {resources.is_local && (
                  <Alert
                    message="Metrics not available in local development mode"
                    type="info"
                    showIcon
                  />
                )}
              </Space>
            </ResourceCard>
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default TenantDetailsPage;
